import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Loader2, Download, Wand2, Eraser, Sparkles, 
  Palette, Sun, SlidersHorizontal, RefreshCw, Check, Image
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OPERATION_ICONS = {
  'remove_background': Eraser,
  'enhance': Sparkles,
  'upscale': Image,
  'style_transform': Palette,
  'color_correct': SlidersHorizontal,
  'denoise': Wand2,
  'relight': Sun,
  'custom': Wand2
};

export default function ImageEditor({ 
  isOpen, 
  onClose, 
  image, 
  onEditComplete 
}) {
  const { token } = useAuth();
  const [operations, setOperations] = useState([]);
  const [styleOptions, setStyleOptions] = useState([]);
  const [lightingOptions, setLightingOptions] = useState([]);
  const [selectedOperation, setSelectedOperation] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedLighting, setSelectedLighting] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [intensity, setIntensity] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [editedImage, setEditedImage] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchOperations();
      setEditedImage(null);
      setSelectedOperation('');
    }
  }, [isOpen]);

  const fetchOperations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/image/edit/operations`);
      setOperations(response.data.operations || []);
      setStyleOptions(response.data.style_options || []);
      setLightingOptions(response.data.lighting_options || []);
    } catch (error) {
      console.error('Failed to fetch operations:', error);
    }
  };

  const handleEdit = async () => {
    if (!selectedOperation) {
      toast.error('Please select an editing operation');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/image/edit/gallery`,
        {
          image_id: image.image_id,
          operation: selectedOperation,
          style: selectedOperation === 'style_transform' ? selectedStyle : null,
          lighting: selectedOperation === 'relight' ? selectedLighting : null,
          custom_instructions: selectedOperation === 'custom' ? customInstructions : null,
          intensity: intensity,
          save_to_gallery: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.images?.length > 0) {
        setEditedImage(response.data.images[0]);
        toast.success(`${response.data.operation_name} applied successfully!`);
        if (onEditComplete) {
          onEditComplete(response.data);
        }
      } else {
        toast.error('Edit failed. Please try again.');
      }
    } catch (error) {
      console.error('Edit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to edit image');
    } finally {
      setLoading(false);
    }
  };

  const downloadImage = () => {
    if (!editedImage) return;
    
    const link = document.createElement('a');
    link.href = `data:${editedImage.mime_type || 'image/png'};base64,${editedImage.data}`;
    link.download = `aira-edited-${selectedOperation}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Edited image downloaded!');
  };

  const resetEditor = () => {
    setEditedImage(null);
    setSelectedOperation('');
    setSelectedStyle('');
    setSelectedLighting('');
    setCustomInstructions('');
    setIntensity(1.0);
  };

  if (!isOpen || !image) return null;

  const OperationIcon = selectedOperation ? OPERATION_ICONS[selectedOperation] || Wand2 : Wand2;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-5xl bg-[var(--bg-card)] rounded-2xl overflow-hidden my-8"
          onClick={(e) => e.stopPropagation()}
          data-testid="image-editor-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Image Editor</h2>
                <p className="text-sm text-[var(--text-tertiary)]">Non-destructive AI editing</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-0">
            {/* Image Preview Section */}
            <div className="p-6 bg-black/20 relative">
              <div className="aspect-square rounded-xl overflow-hidden bg-black/30 flex items-center justify-center">
                {editedImage ? (
                  <div className="relative w-full h-full">
                    <img
                      src={`data:${editedImage.mime_type || 'image/png'};base64,${editedImage.data}`}
                      alt="Edited"
                      className="w-full h-full object-contain"
                      data-testid="edited-image-preview"
                    />
                    {showComparison && (
                      <div 
                        className="absolute inset-0 overflow-hidden"
                        style={{ clipPath: 'inset(0 50% 0 0)' }}
                      >
                        <img
                          src={`data:${image.mime_type || 'image/png'};base64,${image.image_data || image.thumbnail_data}`}
                          alt="Original"
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute top-2 left-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
                          Original
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <img
                    src={`data:${image.mime_type || 'image/png'};base64,${image.image_data || image.thumbnail_data}`}
                    alt="Original"
                    className="w-full h-full object-contain"
                    data-testid="original-image-preview"
                  />
                )}
              </div>

              {/* Comparison Toggle */}
              {editedImage && (
                <div className="mt-4 flex items-center justify-center gap-4">
                  <button
                    onMouseDown={() => setShowComparison(true)}
                    onMouseUp={() => setShowComparison(false)}
                    onMouseLeave={() => setShowComparison(false)}
                    className="px-4 py-2 rounded-lg bg-white/10 text-sm text-white hover:bg-white/20 transition-colors"
                  >
                    Hold to Compare
                  </button>
                  <button
                    onClick={downloadImage}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              )}

              {/* Original prompt */}
              <div className="mt-4 p-3 rounded-lg bg-white/5">
                <p className="text-xs text-[var(--text-tertiary)] mb-1">Original Prompt</p>
                <p className="text-sm text-[var(--text-secondary)] line-clamp-2">{image.prompt}</p>
              </div>
            </div>

            {/* Controls Section */}
            <div className="p-6 space-y-6">
              {/* Operation Selection */}
              <div>
                <label className="text-sm text-[var(--text-tertiary)] mb-3 block">Select Edit Operation</label>
                <div className="grid grid-cols-2 gap-2">
                  {operations.map((op) => {
                    const Icon = OPERATION_ICONS[op.id] || Wand2;
                    return (
                      <button
                        key={op.id}
                        onClick={() => {
                          setSelectedOperation(op.id);
                          setEditedImage(null);
                        }}
                        disabled={loading}
                        className={`flex items-center gap-2 p-3 rounded-xl text-left transition-all ${
                          selectedOperation === op.id
                            ? 'bg-[var(--color-primary)]/20 border border-[var(--color-primary)]/50 text-white'
                            : 'bg-white/5 border border-white/10 text-[var(--text-secondary)] hover:bg-white/10'
                        }`}
                        data-testid={`edit-op-${op.id}`}
                      >
                        <Icon className="w-4 h-4 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{op.name}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Style Options for Style Transform */}
              {selectedOperation === 'style_transform' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="overflow-hidden"
                >
                  <label className="text-sm text-[var(--text-tertiary)] mb-2 block">Choose Style</label>
                  <div className="flex flex-wrap gap-2">
                    {styleOptions.map((style) => (
                      <button
                        key={style.id}
                        onClick={() => setSelectedStyle(style.id)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                          selectedStyle === style.id
                            ? 'bg-[var(--color-secondary)]/30 text-white border border-[var(--color-secondary)]/50'
                            : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                        }`}
                      >
                        {style.name}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Lighting Options for Relight */}
              {selectedOperation === 'relight' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="overflow-hidden"
                >
                  <label className="text-sm text-[var(--text-tertiary)] mb-2 block">Choose Lighting</label>
                  <div className="flex flex-wrap gap-2">
                    {lightingOptions.map((light) => (
                      <button
                        key={light.id}
                        onClick={() => setSelectedLighting(light.id)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                          selectedLighting === light.id
                            ? 'bg-yellow-500/30 text-white border border-yellow-500/50'
                            : 'bg-white/5 text-[var(--text-secondary)] hover:bg-white/10'
                        }`}
                      >
                        {light.name}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Custom Instructions */}
              {selectedOperation === 'custom' && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="overflow-hidden"
                >
                  <label className="text-sm text-[var(--text-tertiary)] mb-2 block">Custom Instructions</label>
                  <textarea
                    value={customInstructions}
                    onChange={(e) => setCustomInstructions(e.target.value)}
                    placeholder="Describe the edits you want..."
                    className="input-field w-full h-24 resize-none"
                    data-testid="custom-instructions-input"
                  />
                </motion.div>
              )}

              {/* Intensity Slider */}
              {selectedOperation && selectedOperation !== 'custom' && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm text-[var(--text-tertiary)]">Effect Intensity</label>
                    <span className="text-sm text-white">{Math.round(intensity * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={intensity}
                    onChange={(e) => setIntensity(parseFloat(e.target.value))}
                    className="w-full accent-[var(--color-primary)]"
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                {editedImage ? (
                  <>
                    <button
                      onClick={resetEditor}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 text-[var(--text-secondary)] hover:bg-white/10 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Edit Again
                    </button>
                    <button
                      onClick={onClose}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
                    >
                      <Check className="w-4 h-4" />
                      Done
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleEdit}
                    disabled={loading || !selectedOperation}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-secondary)] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="apply-edit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Applying Edit...
                      </>
                    ) : (
                      <>
                        <OperationIcon className="w-5 h-5" />
                        Apply {selectedOperation ? operations.find(o => o.id === selectedOperation)?.name : 'Edit'}
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Info */}
              <p className="text-xs text-[var(--text-tertiary)] text-center">
                Edits are non-destructive. Original image is preserved.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
